import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTenant } from '../context/TenantContext';
import './OnboardingBanner.css';

const STEPS = [
  { key: 'linkedin_connected', label: 'Connect LinkedIn', path: '/account' },
  { key: 'limits_configured', label: 'Set Limits', path: '/limits' },
  { key: 'first_campaign_active', label: 'First Campaign', path: '/lists' },
];

const SIDE_QUESTS = [
  { key: 'engagements_visited', label: 'Explore Engagements', path: '/engagements' },
  { key: 'explore_templates', label: 'Explore Templates', path: '/scripts' },
  { key: 'hubspot_connected', label: 'Connect HubSpot', path: '/account' },
];

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2.5 6 5 8.5 9.5 3.5" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="3" y1="3" x2="11" y2="11" />
    <line x1="11" y1="3" x2="3" y2="11" />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 12 12" fill="currentColor" width="10" height="10">
    <path d="M6 1l1.545 3.13L11 4.635 8.5 7.07l.59 3.43L6 8.885 2.91 10.5l.59-3.43L1 4.635l3.455-.505z" />
  </svg>
);

const ProgressRing = ({ done, total }) => {
  const r = 12;
  const circ = 2 * Math.PI * r;
  const offset = circ - (done / total) * circ;
  return (
    <div className="onboarding-progress">
      <svg width="32" height="32" viewBox="0 0 32 32">
        <circle className="onboarding-progress-bg" cx="16" cy="16" r={r} />
        <circle className="onboarding-progress-fill" cx="16" cy="16" r={r}
          strokeDasharray={circ} strokeDashoffset={offset} />
      </svg>
      <span className="onboarding-progress-text">{done}/{total}</span>
    </div>
  );
};

const OnboardingBanner = () => {
  const auth = useAuth();
  const tenant = useTenant() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [checklist, setChecklist] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchChecklist = async () => {
      if (!auth.user?.id_token || !tenant.tenant) return;
      try {
        const { api } = await import('../services/api');
        const resp = await api.get('/v1/onboarding/checklist', {
          headers: { Authorization: `Bearer ${auth.user.id_token}`, tenant: tenant.tenant },
        });
        setChecklist(resp.data);
      } catch (e) {
        // Non-critical
      }
    };
    fetchChecklist();
  }, [auth.user?.id_token, tenant.tenant]);

  if (!checklist || dismissed) return null;

  // Backward compat: older backend responses have no setup_visible — derive it
  // from the pre-Plan-85 contract (dismissed / all_complete).
  const setupVisible = checklist.setup_visible !== undefined
    ? checklist.setup_visible
    : (!checklist.dismissed && !checklist.all_complete);
  const showStepper = !!setupVisible && !checklist.all_complete && !!checklist.steps;

  if (!showStepper) return null;

  const doneCount = STEPS.filter(s => checklist.steps[s.key]).length;
  const totalCount = STEPS.length;
  const activeKey = STEPS.find(s => !checklist.steps[s.key])?.key;

  const sideQuests = checklist.side_quests || {};
  const visibleQuests = SIDE_QUESTS.filter(s => !sideQuests[s.key]);

  const renderStep = (step, index) => {
    const done = checklist.steps[step.key];
    const isActive = !done && step.key === activeKey;
    const status = done ? 'done' : isActive ? 'active' : 'pending';
    const isCurrentPage = location.pathname === step.path;

    return (
      <button key={step.key}
        className={`onboarding-step onboarding-step--${status}`}
        onClick={() => { if (!isCurrentPage) navigate(step.path); }}
        style={isCurrentPage ? { background: 'rgba(139, 92, 246, 0.06)' } : undefined}
      >
        <span className={`onboarding-step-icon onboarding-step-icon--${status}`}>
          {done ? <CheckIcon /> : <span className="onboarding-step-num">{index + 1}</span>}
        </span>
        <span className="onboarding-step-label">{step.label}</span>
      </button>
    );
  };

  return (
    <div className="onboarding-bar">
      {/* Main steps row */}
      <div className="onboarding-bar-inner">
        <ProgressRing done={doneCount} total={totalCount} />
        <span className="onboarding-label">Setup</span>
        <div className="onboarding-divider" />
        <div className="onboarding-steps">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <span className={`onboarding-connector ${
                  checklist.steps[STEPS[i - 1].key] ? 'onboarding-connector--done' : 'onboarding-connector--pending'
                }`} />
              )}
              {renderStep(step, i)}
            </React.Fragment>
          ))}
        </div>
        <button className="onboarding-dismiss" onClick={() => setDismissed(true)} title="Dismiss">
          <CloseIcon />
        </button>
      </div>
      {/* Optional setups row */}
      {visibleQuests.length > 0 && (
        <div className="onboarding-quests">
          <span className="onboarding-quests-label">Optional setup</span>
          {visibleQuests.map(quest => {
            const isCurrentPage = location.pathname === quest.path;
            return (
              <button key={quest.key}
                className={`onboarding-quest${isCurrentPage ? ' onboarding-quest--current' : ''}`}
                onClick={() => { if (!isCurrentPage) navigate(quest.path); }}
              >
                <span className="onboarding-quest-dot" />
                <span className="onboarding-quest-label">{quest.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OnboardingBanner;
